import {
  CreateSingletonInstance,
  CreateSingletonProps,
  Instance,
  Props,
  createSingleton,
  delegate,
} from "tippy.js";
import "tippy.js/dist/tippy.css";
import { PointId } from "../models/map";

export function initializeInteractiveTooltips(
  clickSingleHandler: (pointId: PointId) => any
) {
  const interactiveInstances: Instance[] = [];

  const interactiveSingleton = createSingleton([], {
    moveTransition: "transform 0.2s cubic-bezier(0.22, 0.61, 0.36, 1)",
    theme: "udstillerguide",
    zIndex: 1001,
    allowHTML: true,
    interactive: true,
    appendTo: document.body,
    touch: ["hold", 500],
  });

  const updateInteractiveSingleton = makeSingletonUpdater(interactiveSingleton);

  // Delegat for tooltips
  const tooltipDelegate = delegate("body", {
    target: ".clusterIconTooltip",
    content: (reference) => {
      const content = reference.querySelector("#clusterTooltips");
      return content || "";
    },
    onCreate: (instance) => {
      makeAddInstanceToSingletonHandler(
        interactiveInstances,
        updateInteractiveSingleton
      )(instance);
      const innerButton = instance.popper.querySelector("#clusterTooltips");
      if (innerButton) {
        innerButton.addEventListener("click", function (event) {
          // Typeassertion til HTMLElement
          let target = event.target as HTMLElement | null;
          let dataIdElement: HTMLElement | null = null;
          let pointId: PointId | null = null;
          while (target) {
            if (target.hasAttribute("data-id")) {
              dataIdElement = target;
              const pointString = target.getAttribute("data-id");
              const pointInt = parseInt(pointString ?? "0");
              pointId = { id: pointInt };
              break;
            }
            target = target.parentElement;
          }
          if (dataIdElement) {
            const pointString = dataIdElement.getAttribute("data-id");
            const pointInt = parseInt(pointString ?? "0");
            const pointId: PointId = { id: pointInt };

            // Find the parent element with the tippy instance
            let parent = dataIdElement.parentElement;
            while (parent) {
              const tippyInstance = (parent as any)._tippy;
              if (tippyInstance) {
                tippyInstance.hide();
                break;
              }
              parent = parent.parentElement;
            }
            clickSingleHandler(pointId);
          }
        });
      }
    },
    onDestroy: (instance) => {
      makeRemoveInstanceFromSingletonHandler(
        interactiveInstances,
        updateInteractiveSingleton
      )(instance);
    },
  });
}

export function initializeTooltips() {
  const nonInteractiveInstances: Instance[] = [];

  const nonInteractiveSingleton = createSingleton([], {
    delay: 100,
    moveTransition: "transform 0.2s cubic-bezier(0.22, 0.61, 0.36, 1)",
    theme: "udstillerguide",
    zIndex: 10000001,
    allowHTML: true,
    interactive: false,
    touch: ["hold", 500],
  });

  const updateNonInteractiveSingleton = makeSingletonUpdater(
    nonInteractiveSingleton
  );

  // Delegat for tooltips
  const tooltipDelegate = delegate("body", {
    target: ".tooltip",
    content: (reference) => {
      return (reference as HTMLElement).dataset.tippyContent || "";
    },
    onCreate: (instance) => {
      makeAddInstanceToSingletonHandler(
        nonInteractiveInstances,
        updateNonInteractiveSingleton
      )(instance);
    },
    onDestroy: (instance) => {
      makeRemoveInstanceFromSingletonHandler(
        nonInteractiveInstances,
        updateNonInteractiveSingleton
      )(instance);
    },
  });
}

function makeSingletonUpdater(
  singleton: CreateSingletonInstance<CreateSingletonProps<Props>>
) {
  return function (instanceList: Instance[]) {
    singleton.setInstances(instanceList);
  };
}

function makeAddInstanceToSingletonHandler(
  instanceList: Instance[],
  updateSingleton: (instances: Instance[]) => void
) {
  return function (tippyInstance: Instance) {
    // add instance to instances list
    if (tippyInstance.reference == document.body) return; // ignore the body item that represents the delegate
    instanceList.push(tippyInstance);
    updateSingleton(instanceList);
  };
}

function makeRemoveInstanceFromSingletonHandler(
  instanceList: Instance[],
  updateSingleton: (instances: Instance[]) => void
) {
  return function (tippyInstance: Instance) {
    // remove instance from instances list
    const instanceIndex = instanceList.indexOf(tippyInstance);
    instanceList.splice(instanceIndex, 1);
    updateSingleton(instanceList);
  };
}
